/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {useEffect} from 'react';
import {
  PhotoFormControllerProvider,
  usePhotoFormController,
} from 'src/photoForm/PhotoFormController';
import {PhotoSelectionScreen} from 'src/photoSelection/PhotoSelectionScreen';
import {SubmissionErrorScreen} from 'src/photoSubmission/SubmissionErrorScreen';
import {SubmissionLoadingScreen} from 'src/photoSubmission/SubmissionLoadingScreen';
import {SubmissionSuccessScreen} from 'src/photoSubmission/SubmissionSuccessScreen';
import {ErrorDisplayContainer} from 'src/theming/components/ErrorDisplayContainer';

export const PhotoForm = () => {
  return (
    <PhotoFormControllerProvider>
      <PhotoFormImpl />
      <ErrorDisplayContainer />
    </PhotoFormControllerProvider>
  );
};

const PhotoFormImpl = () => {
  const {
    formState: {isDirty, isSubmitting, isSubmitSuccessful, isSubmitted},
  } = usePhotoFormController();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Block navigation if the form is dirty so an accidental browser navigation
      // (i.e. a refresh) doesn't lose all of the form state.
      event.preventDefault();
    };

    // MDN recommends listening to beforeunload only when we want to block navigation.
    // This will not work on iOS because WebKit does not support the event on iOS.
    if (isDirty && !isSubmitted) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, isSubmitted]);

  if (isSubmitting) {
    return <SubmissionLoadingScreen />;
  } else if (isSubmitted && isSubmitSuccessful) {
    return <SubmissionSuccessScreen />;
  } else if (isSubmitted && !isSubmitSuccessful) {
    return <SubmissionErrorScreen />;
  } else {
    return <PhotoSelectionScreen />;
  }
};
